import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, X } from "lucide-react";

const UpgradeContext = createContext(null);

// Module-level setter so non-React code (like the axios interceptor or
// PlayerContext) can also trigger the modal without having to live inside
// the React tree. Supports an options object: { ctaLabel, ctaPath, body }.
let externalShow = () => {};
export function showUpgradeExternally(message, options) {
  try { externalShow(message, options); } catch { /* ignore */ }
}

// Event name broadcast when the UpgradePrompt's CTA is about to navigate away;
// the PlayerContext listens for it and collapses the fullscreen player so the
// destination page (sign-up / Stripe checkout) is actually visible.
export const LEAVE_PLAYER_EVENT = "dtm-leave-player";

// What Premium includes. The same list is shown on the Account page (pages/Account.js).
const PERKS = [
  "Full-length premium songs",
  "The DJ Booth, to mix your own videos and music on your TV",
  "Favorites and Recently Played",
  "Multiple playlists with 100+ songs",
  "Click-and-drag to reorganize songs in playlists",
  "The Quartz look for buttons and bars",
];

// With no body text, the pop-up lists what Premium includes.
const DEFAULTS = {
  ctaLabel: "Upgrade to Premium",
  ctaPath: "/account?checkout=1",   // the Account page sends a free member straight on to Stripe
  body: null,
};

export function UpgradeProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("This is a premium feature.");
  const [opts, setOpts] = useState(DEFAULTS);
  const nav = useNavigate();

  const showUpgrade = useCallback((msg, options) => {
    setMessage(msg && typeof msg === "string" ? msg : "This is a premium feature.");
    setOpts({ ...DEFAULTS, ...(options || {}) });
    setOpen(true);
  }, []);

  const close = () => setOpen(false);
  const cta = () => { setOpen(false); window.dispatchEvent(new Event(LEAVE_PLAYER_EVENT)); nav(opts.ctaPath || DEFAULTS.ctaPath); };

  useEffect(() => { externalShow = showUpgrade; return () => { externalShow = () => {}; }; }, [showUpgrade]);

  return (
    <UpgradeContext.Provider value={{ showUpgrade }}>
      {children}
      {open && (
        <div
          data-testid="upgrade-prompt"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={close}
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-amber-400/40 bg-gradient-to-br from-zinc-900 via-zinc-900 to-amber-500/5 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="upgrade-prompt-close"
              onClick={close}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 text-zinc-400"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 text-amber-400 mb-3">
              <Sparkles className="w-5 h-5" />
              <span className="font-mono-ui text-xs uppercase tracking-widest">{opts.ctaPath === "/register" ? "No credit card needed" : "Premium only"}</span>
            </div>
            <h2 className="font-display text-2xl font-extrabold mb-2" data-testid="upgrade-prompt-message">{message}</h2>
            {opts.body ? (
              <p className="text-sm text-zinc-400 mb-6">{opts.body}</p>
            ) : (
              <div className="mb-6" data-testid="upgrade-prompt-perks">
                <p className="text-sm text-zinc-400 mb-3">Premium gives you:</p>
                <ul className="space-y-2 text-sm text-zinc-300">
                  {PERKS.map((perk) => (
                    <li key={perk} className="flex gap-2"><Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {perk}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                data-testid="upgrade-prompt-not-now"
                onClick={close}
                className="px-4 py-2 rounded-full border border-white/15 text-sm hover:bg-white/5"
              >
                Not now
              </button>
              <button
                data-testid="upgrade-prompt-upgrade"
                onClick={cta}
                className="px-4 py-2 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300"
              >
                {opts.ctaLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </UpgradeContext.Provider>
  );
}

export function useUpgrade() {
  const ctx = useContext(UpgradeContext);
  return ctx || { showUpgrade: showUpgradeExternally };
}
